/* MemberShell — minimal logged-in header for /member/* routes.
   Per user requirement: hide site nav inside member area, show only:
   logo · Manage subscription · Logout.
   Open Stripe customer portal via /api/subscriptions/portal. */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CreditCard, LogOut, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { apiLogout, authHeaders } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEMO_PATH = "/ig/content/member/demo";

export default function MemberShell({ email, children }) {
    const nav = useNavigate();
    const location = useLocation();
    const [opening, setOpening] = useState(false);
    const isDemo = (email || "").startsWith("igdemo@");

    // Demo account is locked to the demo showcase — never the real member area.
    useEffect(() => {
        if (isDemo && location.pathname.startsWith("/member")) nav(DEMO_PATH, { replace: true });
    }, [isDemo, location.pathname]); // eslint-disable-line

    const openPortal = async () => {
        if (!email) return;
        try {
            setOpening(true);
            const r = await axios.post(
                `${API}/subscriptions/portal`,
                { email, origin_url: window.location.origin },
                { headers: authHeaders() },
            );
            if (r.data?.portal_url) window.location.href = r.data.portal_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open Stripe portal");
        } finally {
            setOpening(false);
        }
    };

    const logout = async () => {
        await apiLogout();
        toast.success("Logged out");
        nav(isDemo ? DEMO_PATH : "/member/login", { replace: true });
        if (isDemo) window.location.reload();
    };

    return (
        <div className="min-h-screen bg-cafe-cream">
            {isDemo && (
                <div data-testid="demo-mode-banner" className="bg-[#C9A227] text-cafe-espresso text-center text-[12px] font-semibold py-2 px-4">
                    演示模式 DEMO — 此账号内容仅为示例，供你预览会员中心功能 · Sample data for preview only, not a real account
                </div>
            )}
            <header className="sticky top-0 z-40 bg-cafe-cream/90 backdrop-blur border-b border-border" data-testid="member-shell-header">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4">
                    <Link to="/member" className="font-serif text-xl text-cafe-espresso hover:text-cafe-olive transition-colors" data-testid="member-logo-link">
                        kaily<span className="text-cafe-terracotta">.</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={openPortal}
                            disabled={opening || !email}
                            className="bg-white border border-border hover:border-cafe-olive text-cafe-espresso rounded-full px-3 sm:px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="member-manage-sub-btn"
                        >
                            {opening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                            <span className="hidden sm:inline">Manage subscription</span>
                            <span className="sm:hidden">Billing</span>
                        </button>
                        <button
                            onClick={logout}
                            className="text-cafe-mocha hover:text-cafe-terracotta rounded-full p-2"
                            data-testid="member-logout-btn"
                            title="Log out"
                        >
                            <LogOut className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
        </div>
    );
}
